import { DateTime } from "luxon"
import { EventDropArg } from "@fullcalendar/core"
import { EventResizeDoneArg } from "@fullcalendar/interaction"

import { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { DeliveryWithCart } from "../../form/formTypes"
import { isAllDayAvailabilitySlot } from "../calendar/helpers"

import { TimelineCalendarEvent, TimelineCalendarEventTimeRange } from "../calendar/fullCalendarTypes"

export const LOCAL_EVENT = "local-"
const LOCAL_EVENT_COLOR = tailwindPalette.blue[300]

const EVENT_COLOR_OUT_OF_SERVICE = tailwindPalette.slate[300]
const EVENT_COLOR_CAN_EDIT = "#80CBC4"
const EVENT_COLOR_DEFAULT = tailwindPalette.red[300]

export function getEventBackgroundColor(event: { outOfService?: boolean; canEdit?: boolean }): string {
    if (event.outOfService) return EVENT_COLOR_OUT_OF_SERVICE
    if (event.canEdit) return EVENT_COLOR_CAN_EDIT
    return EVENT_COLOR_DEFAULT
}

export const DEFAULT_DURATION_MINUTES = 30
export const DEFAULT_MINIMUM_DURATION_MINUTES = 5

export const isLocalEvent = (id: string) => id?.startsWith(LOCAL_EVENT)
export const isCanEditExistOrder = (info: EventDropArg | EventResizeDoneArg) => !!info.event?.extendedProps["canEdit"]

export const createNewEvent = (
    resourceId: string,
    start: string,
    end: string,
    timeRange?: TimelineCalendarEventTimeRange,
): TimelineCalendarEvent => ({
    id: `${LOCAL_EVENT}${resourceId}-${start}`,
    resourceIds: [resourceId],
    start,
    end,
    backgroundColor: LOCAL_EVENT_COLOR,
    timeRange,
})

export const createInitEvents = (resourceIds: string[], start: string, end: string): TimelineCalendarEvent => {
    const firstResourceId = resourceIds[0] || "book-period"

    return {
        id: `${LOCAL_EVENT}${firstResourceId}-${start}`,
        resourceIds,
        start,
        end,
        backgroundColor: LOCAL_EVENT_COLOR,
    }
}

export function roundDateTimeToNearestMinutes(dateTime: DateTime, nearestMinutes: number): DateTime {
    const rounded = Math.ceil(dateTime.minute / nearestMinutes) * nearestMinutes
    return dateTime.set({ minute: rounded, second: 0, millisecond: 0 })
}

function roundUpNowForBooking(now: DateTime, intervalMinutes: number): DateTime {
    const roundedNowBase = now.second !== 0 || now.millisecond !== 0 ? now.startOf("minute").plus({ minutes: 1 }) : now
    const remainder = roundedNowBase.minute % intervalMinutes
    if (remainder === 0) return roundedNowBase.set({ second: 0, millisecond: 0 })
    return roundedNowBase.plus({ minutes: intervalMinutes - remainder }).set({ second: 0, millisecond: 0 })
}

export function getBookingRangeFromAvailabilitySlot(
    args: Readonly<{
        dateTimeFactory: DateTimeFactory
        slotStart: DateTime
        slotEnd: DateTime
        now: DateTime
        intervalMinutes: number
        allDayStartHour: number
        maxDurationHours: number
    }>,
): { effectiveStart: DateTime; adjustedEnd: DateTime; originalEnd: DateTime } | null {
    const { dateTimeFactory, slotStart, slotEnd, now, intervalMinutes, allDayStartHour, maxDurationHours } = args

    if (!slotStart.isValid || !slotEnd.isValid) return null
    if (!(slotEnd > slotStart)) return null

    // Slot is entirely in the past -> do not allow booking.
    if (slotEnd <= now) return null

    const roundedNow = roundUpNowForBooking(now, intervalMinutes)

    const startBase = isAllDayAvailabilitySlot(dateTimeFactory, slotStart, slotEnd)
        ? slotStart.set({ hour: allDayStartHour, minute: 0, second: 0, millisecond: 0 })
        : slotStart

    const effectiveStart = startBase < now ? roundedNow : startBase
    if (effectiveStart >= slotEnd) return null

    const desiredEnd = effectiveStart.plus({ hours: maxDurationHours })
    const adjustedEnd = desiredEnd < slotEnd ? desiredEnd : slotEnd

    return { effectiveStart, adjustedEnd, originalEnd: slotEnd }
}

export function earliestTime(times: DeliveryWithCart[]): string {
    return times.map((t) => t.time!).sort((a, b) => a.localeCompare(b))[0]
}
